import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Search, ChevronDown, ChevronUp } from 'lucide-react';

interface Option {
  value: string;
  label: string;
  disabled?: boolean;
}

interface SearchableSelectProps {
  options: Option[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  className?: string;
}

// Limit initial display to prevent performance issues with large lists
const INITIAL_DISPLAY_LIMIT = 20;
const MAX_SEARCH_RESULTS = 50;

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = "Sélectionner...",
  searchPlaceholder = "Rechercher...",
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when menu opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Memoize non-disabled options
  const availableOptions = useMemo(() =>
    options.filter(option => !option.disabled),
    [options]
  );

  // Filter options based on search term with memoization
  const filteredOptions = useMemo(() => {
    if (search.length === 0) {
      return availableOptions;
    }
    const searchLower = search.toLowerCase();
    return availableOptions
      .filter(option => option.label.toLowerCase().includes(searchLower))
      .slice(0, MAX_SEARCH_RESULTS);
  }, [availableOptions, search]);

  // Get all header options (disabled options that serve as category headers)
  const headerOptions = useMemo(() =>
    options.filter(option => option.disabled),
    [options]
  );

  // Get the selected option
  const selectedOption = useMemo(() =>
    options.find(option => option.value === value),
    [options, value]
  );

  // Determine which options to display (limited when no search)
  const displayOptions = useMemo(() => {
    if (search.length > 0) {
      return filteredOptions;
    }
    return filteredOptions.slice(0, INITIAL_DISPLAY_LIMIT);
  }, [filteredOptions, search]);

  const totalAvailableOptions = availableOptions.length;
  const showLimitMessage = search.length === 0 && totalAvailableOptions > INITIAL_DISPLAY_LIMIT;

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full h-9 px-3 border border-gray-300 rounded-lg bg-white text-left text-sm flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <span className="truncate">
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        {isOpen ? (
          <ChevronUp className="h-4 w-4 text-gray-500" />
        ) : (
          <ChevronDown className="h-4 w-4 text-gray-500" />
        )}
      </button>

      {isOpen && (
        <div className="absolute w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-[1000]">
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                ref={searchInputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={searchPlaceholder}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="max-h-60 overflow-y-auto">
            {filteredOptions.length === 0 && search.length > 0 ? (
              <div className="px-3 py-2 text-gray-500 text-center">
                Aucun résultat
              </div>
            ) : (
              <>
                {/* Toujours afficher les en-têtes de catégorie */}
                {headerOptions.map((option, index) => (
                  <div
                    key={`header-${index}`}
                    className="px-3 py-2 text-xs font-semibold text-gray-500 bg-gray-50"
                  >
                    {option.label}
                  </div>
                ))}

                {/* Afficher les options limitées pour de meilleures performances */}
                {displayOptions.map(option => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      onChange(option.value);
                      setIsOpen(false);
                      setSearch("");
                    }}
                    className={`w-full px-3 py-2 text-left hover:bg-gray-100 ${
                      option.value === value ? 'bg-blue-50 text-blue-800' : ''
                    }`}
                  >
                    {option.label}
                  </button>
                ))}

                {/* Afficher un message si plus d'options sont disponibles */}
                {showLimitMessage && (
                  <div className="px-3 py-2 text-xs text-gray-500 text-center bg-gray-50 border-t">
                    Affichage de {INITIAL_DISPLAY_LIMIT} sur {totalAvailableOptions} options.
                    Tapez pour rechercher parmi toutes.
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;